import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PARTNER_TOOLS, ALL_TOOL_KEYS } from "@/lib/partnerTools";
import { Handshake, Loader2, Sparkles } from "lucide-react";

const PROFILES = [
  { key: "minimo", label: "Minimo", hint: "Solo le 4 funzioni principali" },
  { key: "completo", label: "Completo", hint: "Tutti gli strumenti" },
  { key: "personalizzato", label: "Personalizzato", hint: "Scelta card per card" },
];
const keysFor = (p) => (p.dashboard_profile === "minimo" ? [] : p.dashboard_profile === "personalizzato" ? (p.dashboard_tools || []) : ALL_TOOL_KEYS);

export function DashboardToolsDialog({ partner, onClose, onSaved }) {
  const [profile, setProfile] = useState("completo");
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);
  const [novita, setNovita] = useState(false);
  const [rete, setRete] = useState(false);
  useEffect(() => { if (partner) { setProfile(partner.dashboard_profile || "completo"); setSel(keysFor(partner)); setNovita(!!partner.vede_novita); setRete(!!partner.rete_solidale_access); } }, [partner]);

  const pick = (k) => { setProfile(k); if (k !== "personalizzato") setSel(k === "minimo" ? [] : ALL_TOOL_KEYS); };
  const toggle = (k) => {
    const next = sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k];
    setSel(next);
    setProfile(next.length === 0 ? "minimo" : next.length === ALL_TOOL_KEYS.length ? "completo" : "personalizzato");
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/partners/${partner.id}`, { dashboard_profile: profile, dashboard_tools: sel, vede_novita: novita, rete_solidale_access: rete });
      toast.success(`Dashboard aggiornata per ${partner.ragione_sociale}`);
      onClose(); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!partner} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="dashboard-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">Dashboard — {partner?.ragione_sociale}</DialogTitle>
          <DialogDescription>Nuova trattativa, Pratiche aperte, Anagrafiche e Proposte emesse sono sempre visibili. Qui scegli gli strumenti in più.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {PROFILES.map((p) => (
            <button key={p.key} type="button" data-testid={`dash-profile-${p.key}`} onClick={() => pick(p.key)}
              className={`rounded-lg border p-2.5 text-left transition-colors ${profile === p.key ? "border-forest bg-forest text-white" : "border-border hover:bg-sage/40"}`}>
              <div className="text-sm font-semibold">{p.label}</div>
              <div className={`text-[11px] ${profile === p.key ? "text-white/80" : "text-muted-foreground"}`}>{p.hint}</div>
            </button>
          ))}
        </div>
        <div className="space-y-1 py-2">
          {PARTNER_TOOLS.map((t) => (
            <label key={t.key} data-testid={`dash-tool-${t.key}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-sage/40">
              <Checkbox checked={sel.includes(t.key)} onCheckedChange={() => toggle(t.key)} />
              <t.icon className="h-4 w-4 text-forest/70" />
              <span className="flex-1 text-sm text-forest">{t.label}{t.agencyOnly ? " (solo Agenzia)" : ""}</span>
            </label>
          ))}
          <label data-testid="dash-vede-novita" className="mt-2 flex items-center gap-3 rounded-lg border-t px-2 pt-3 pb-2 hover:bg-sage/40">
            <Checkbox checked={novita} onCheckedChange={(v) => setNovita(!!v)} />
            <Sparkles className="h-4 w-4 text-forest/70" />
            <span className="flex-1 text-sm text-forest">Vede le novità di versione all'accesso<span className="block text-[11px] text-muted-foreground">Finestra con "OK, ho preso visione"</span></span>
          </label>
          <label data-testid="dash-rete-solidale" className="flex items-center gap-3 rounded-lg border border-terracotta/30 bg-sage/30 px-2 py-2 hover:bg-sage/50">
            <Checkbox checked={rete} onCheckedChange={(v) => setRete(!!v)} data-testid="dash-rete-solidale-check" />
            <Handshake className="h-4 w-4 text-terracotta" />
            <span className="flex-1 text-sm text-forest">Accesso Rete Solidale<span className="block text-[11px] text-muted-foreground">Agisce come l'Agenzia su Accordi, Convenzioni e Vetrina: crea e modifica, sconti, verifiche, disdette, referenti, report</span></span>
          </label>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="dash-save" className="bg-terracotta hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
